import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModal } from '../../../../../../actions';

const AdminSettingMetadataMappingMapSetting = ({
  clientSettings,
  setting,
  availableTags,
  taxonomies,
  customFields,
  handleMapSettingChange,
  handleDeleteMapSetting,
  handleInsertFieldModal,
}) => {
  const intl = useIntl();

  const options = [
    {
      label: intl.formatMessage({ defaultMessage: 'General fields' }),
      options: [
        { value: 'filename', label: intl.formatMessage({ defaultMessage: 'File name' }) },
        { value: 'description', label: intl.formatMessage({ defaultMessage: 'File description' }) },
        { value: 'taxonomy-all', label: intl.formatMessage({ defaultMessage: 'All Tag Categories' }) },
      ],
    },
    {
      label: intl.formatMessage({ defaultMessage: 'Tag Categories' }),
      options: taxonomies.map(t => ({ value: `taxonomy-${t.id}`, label: t.name })),
    },
    {
      label: intl.formatMessage({ defaultMessage: 'Custom Fields' }),
      options: customFields.map(f => ({ value: `custom-field-${f.id}`, label: f.name })),
    },
  ];

  const flatOptions = options.map(group => group.options).flat();

  const handleTagChange = (tag) => {
    handleMapSettingChange({
      ...setting,
      tag,
    });
  };

  const handleMappingChange = (mapping) => {
    handleMapSettingChange({
      ...setting,
      mapping,
    });
  };

  const handleSeparatorChange = (separator) => {
    handleMapSettingChange({
      ...setting,
      separator,
    });
  };

  const handleSimpleChange = () => {
    if (!setting.simple) {
      handleMapSettingChange({
        ...setting,
        mapping: options[0].value,
        simple: !setting.simple,
      });
    } else {
      handleMapSettingChange({
        ...setting,
        mapping: `{{${setting.mapping}}}`,
        simple: !setting.simple,
      });
    }
  };

  const handleInsertField = (field) => {
    handleMappingChange(`${setting.mapping}${field}`);
  };

  const currentTag =
    clientSettings.download_file_available_exif_tags.find(s => s.key === setting.tag);

  return (
    <tr>
      <td className="metadata-mapping">
        {setting.simple && (
          <Select
            options={options}
            onChange={option => handleMappingChange(option.value)}
            value={flatOptions.find(o => setting.mapping === o.value)}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            className="metadata-mapping-select"
          />
        )}
        {!setting.simple && (
          <>
            <Input
              type="text"
              value={setting.mapping}
              onChange={(e) => handleMappingChange(e.target.value)}
            />
            <Input
              type="text"
              value={setting.separator || ', '}
              onChange={(e) => handleSeparatorChange(e.target.value)}
              className="metadata-mapping-separator"
              id={`MapSeparator-${setting.key}`}
              style={{ whiteSpace: 'pre' }}
            />
            <UncontrolledTooltip fade={false} placement="bottom" target={`MapSeparator-${setting.key}`}>
              {intl.formatMessage({ defaultMessage: 'Input your tag separator' })}
            </UncontrolledTooltip>
            <Button
              onClick={() => handleInsertFieldModal(options, flatOptions, handleInsertField)}
              id={`HandleInsertField-${setting.key}`}
              className="metadata-mapping-simple-change"
              color="secondary"
            >
              {intl.formatMessage({ defaultMessage: 'Insert field' })}
            </Button>
            <UncontrolledTooltip fade={false} placement="bottom" target={`HandleSimpleChange-${setting.key}`}>
              {intl.formatMessage({ defaultMessage: 'Select custom field or category' })}
            </UncontrolledTooltip>
          </>
        )}
        <Button
          onClick={() => handleSimpleChange()}
          id={`HandleSimpleChange-${setting.key}`}
          color="primary"
        >
          {setting.simple ? <i className="far fa-input-text" /> : <i className="far fa-input-pipe" />}
        </Button>
        <UncontrolledTooltip fade={false} placement="bottom" target={`HandleSimpleChange-${setting.key}`}>
          {
            setting.simple ?
              intl.formatMessage({ defaultMessage: 'Enable advanced mode' }) :
              intl.formatMessage({ defaultMessage: 'Disable advanced mode' })
          }
        </UncontrolledTooltip>
      </td>
      <td className="metadata-mapping-icon">
        <span>
          <i className="far fa-arrow-right fa-xl" />
        </span>
      </td>
      <td>
        <Input
          type="select"
          className="custom-select"
          defaultValue={setting.tag}
          onChange={e => handleTagChange(e.target.value)}
        >
          {currentTag && (
            <option key={currentTag.key} value={currentTag.key}>{currentTag.tag}</option>
          )}
          {availableTags.map(s => (
            <option key={s.key} value={s.key}>{s.tag}</option>
          ))}
        </Input>
      </td>
      <td className="action-more">
        <Button
          onClick={() => handleDeleteMapSetting(setting.key)}
          id={`MapSettingDelete-${setting.key}`}
          color="secondary"
        >
          <i className="fas fa-trash" />
        </Button>
        <UncontrolledTooltip fade={false} placement="bottom" target={`MapSettingDelete-${setting.key}`}>
          {intl.formatMessage({ defaultMessage: 'Delete' })}
        </UncontrolledTooltip>
      </td>
    </tr>
  );
};

const mapStateToProps = state => ({
  clientSettings: state.App.settings,
  customFields: state.Metadata.customFields,
  taxonomies: state.Metadata.taxonomies,
});

const mapDispatchToProps = dispatch => ({
  handleInsertFieldModal: (options, flatOptions, handleInsertField) => {
    dispatch(showModal('MetadataMappingInsertFieldModal', {
      className: 'metadata-mapping-insert-field',
      options,
      flatOptions,
      handleInsertField,
    }));
  },
});

AdminSettingMetadataMappingMapSetting.propTypes = {
  clientSettings: PropTypes.object.isRequired,
  setting: PropTypes.object.isRequired,
  availableTags: PropTypes.array.isRequired,
  customFields: PropTypes.array.isRequired,
  handleMapSettingChange: PropTypes.func.isRequired,
  handleDeleteMapSetting: PropTypes.func.isRequired,
  handleInsertFieldModal: PropTypes.func.isRequired,
  taxonomies: PropTypes.array.isRequired,
};

export default
connect(mapStateToProps, mapDispatchToProps)(AdminSettingMetadataMappingMapSetting);
